import { Component, Input, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddDocument } from 'src/app/interfaces/AdmissionPatient/AdmissionProfile/IAddDocument';
import { IAdmissionFile } from 'src/app/interfaces/AdmissionPatient/AdmissionProfile/IAdmissionFile';
import { PatientService } from 'src/app/services/patient.service';
import { COLORS } from 'src/styles/colors';
import Swal from 'sweetalert2';

type PreviewState = {
  visible: boolean;
  title: string;
  src: string | null; // blob URL or direct URL
  kind: 'image' | 'pdf' | 'unknown';
};

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.css'],
  standalone: false,
})
export class DocumentComponent {
  @Input() patientIdInput?: string | null;
  @Input() document?: IAdmissionFile;
  @Input() fileTypeId?: string;
  @Input() isEditMode: boolean = false;

  patientId: string = '';

  preview: PreviewState = {
    visible: false,
    title: '',
    src: null,
    kind: 'unknown',
  };

  constructor(
    private readonly patientService: PatientService,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    if (!this.patientIdInput) {
      this.route.queryParams.subscribe((params) => {
        this.patientId = params['patientId'];
      });
    } else {
      this.patientId = this.patientIdInput!;
    }
  }

  ngOnChanges(changes: SimpleChanges) {}

  // ---------- Helpers ----------
  trackByFile = (_: number, f: { id: number | string }) => f.id;

  fileName(path: string): string {
    try {
      const u = path.split('?')[0]; // strip query if any
      return u.split('/').pop() || u;
    } catch {
      return path;
    }
  }

  isPdf(path: string): boolean {
    return /\.pdf(\?|$)/i.test(path);
  }

  isImage(path: string): boolean {
    return /\.(png|jpe?g|webp|gif|bmp|svg)(\?|$)/i.test(path);
  }

  canPreview(path: string): boolean {
    return this.isPdf(path) || this.isImage(path);
  }

  // ---------- View (no download) ----------
  // inside DocumentComponent
  viewDocument(f: { id: number; path: string }) {
    if (!this.canPreview(f.path)) {
      this.swalToastService.toastError(
        this.translate.instant('Preview not supported for this file type.')
      );
      return;
    }
    this.patientService.viewDocumentOpenTab(f.id.toString(), this.patientId);
  }

  closePreview() {
    if (this.preview.src) {
      URL.revokeObjectURL(this.preview.src);
    }
    this.preview = { visible: false, title: '', src: null, kind: 'unknown' };
  }

  // ---------- Download (download only) ----------
  downloadDocument(path: string, did: string) {
    const name = this.fileName(path);
    this.patientService.downloadDocument(name, did, this.patientId);
  }

  // ---------- Delete (edit mode only) ----------
  deleteDocument(did: string) {
    Swal.fire({
      title: this.translate.instant(
        'Are you sure you want to delete this document?'
      ),
      text: this.translate.instant(
        'You will not be able to revert this change.'
      ),
      icon: 'warning',
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Delete')}`,
      confirmButtonColor: COLORS.green1,
      cancelButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        this.patientService.deleteDocument(did, this.patientId).subscribe({
          next: () => {
            Swal.fire({
              title: this.translate.instant('Deleted!'),
              text: this.translate.instant('Document has been deleted.'),
              icon: 'success',
            });
            this.patientService.getFiles(this.patientId); // refresh list
          },
          error: (err) => {
            Swal.fire({
              title: this.translate.instant('Error!'),
              text: this.translate.instant(
                'There was an error deleting the document.'
              ),
              icon: 'error',
            });
            console.error('Error deleting document:', err);
          },
        });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire({
          title: this.translate.instant('Cancelled'),
          text: this.translate.instant('Your document has NOT been deleted'),
          icon: 'info',
          confirmButtonColor: COLORS.green1,
        });
      }
    });
  }

  // ---------- Upload ----------
  onFileSelected(event: any) {
    const files: File[] = Array.from(event.target.files || []);
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
    ];

    const invalidFile = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidFile) {
      this.swalToastService.toastError('Only PDF and image files are allowed');
      event.target.value = '';
      return;
    }

    if (files.length) {
      const documentObj: IAddDocument = {
        files,
        fileTypeId: this.fileTypeId,
        patientId: this.patientId,
      };

      this.patientService.addDocument(documentObj).subscribe({
        next: (created) => {
          this.swalToastService.toastSuccess('Document added successfully');
          if (this.document && created?.filePaths?.length) {
            this.document.filePaths = [
              ...this.document.filePaths,
              ...created.filePaths,
            ];
          }
          this.patientService.getFiles(this.patientId); // ensure eventual consistency
        },
        error: (err) => {
          this.swalToastService.toastError('Unable to upload document');
          console.error('Error adding document:', err);
        },
      });
    }
  }
}
